import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import get from 'lodash/get';
import { Element } from 'react-scroll';

import { findValidDelivery } from 'Redux/ducks/deliveries';
import { findValidFreeProduct } from 'Redux/ducks/free-product';
import { findValidVoucher } from 'Redux/ducks/vouchers';
import FormatPartOfBundle from 'Components/Product/ProductOffers/FormatPartOfBundle';
import FreeProductContainer from 'Containers/Product/FreeProductContainer';
import PartOfBundle from 'Components/Product/ProductOffers/PartOfBundle';
import PreorderBonus from 'Components/Product/ProductOffers/PreorderBonus';
import ProductRequirements from 'Helpers/Product/ProductRequirements';
import ValidCoupon from 'Components/Product/ProductOffers/ValidCoupon';
import VoucherDelivery from 'Components/Product/ProductOffers/VoucherDelivery';
import EmailSubscribeOffer from 'Components/Product/ProductOffers/EmailSubscribeOffer';
import SiteSlider from 'Components/Utility/SiteSlider/SiteSlider';
import StaticBannerOffer from 'Components/Product/ProductOffers/StaticBannerOffer';
import EmailSignupPlacement from 'Components/Banner/EmailSignupPlacement/EmailSignupPlacement';
import LinkBannerOffer from 'Components/Product/ProductOffers/LinkBannerOffer';
import CouponBannerOffer from 'Components/Product/ProductOffers/CouponBannerOffer';

import 'Components/Product/ProductOffers/ProductOffers.scss';

function ProductOffers({
  product,
  deliveries,
  freeProduct,
  vouchers,
  title,
  emailNewsletter,
  competitions,
}) {
  if (product.presale || product.isPreview) return false;

  // TODO in the future we need to get competitions better than [0]
  const competition = competitions[0] || {};

  const categories = get(product, 'categories', []);
  const collections = get(product, 'collections', []);

  const delivery = findValidDelivery(product, deliveries);
  const validPromotions = findValidFreeProduct(product, freeProduct, true);
  const validVouchers = findValidVoucher(product, vouchers);
  const partOfBundle = FormatPartOfBundle(
    product.in_bundles,
    product.firstPickAndMixTier,
    product.nextPickAndMixTier,
    product.currentSaveMore,
  );
  const dates = ProductRequirements.ReleaseDates(product);

  let showMysteryBanner = false;
  if (product.slug === 'birthday-mystery-bundle') showMysteryBanner = true;

  const showPreorderBonus = (
    product.preorder_bonus_banner && dates.isPreorder
  ) || (dates.earlyAdopterBonus && product.preorder_bonus_banner);

  const showCrossSellBanner = categories.some(category => category.name && category.name.toLowerCase() === 'supernatural');
  let showCouponBanner = collections.some(collection => collection.name && collection.name.toLowerCase() === 'fantasyverse');

  const lunarSaleEnds = '2025-02-02T11:00:00.000Z';
  if (Date.now() > Date.parse(lunarSaleEnds)) {
    showCouponBanner = false;
  }

  const offers = [];

  if (!isEmpty(competition)) {
    offers.push({ type: 'competition', order: offers.length + 1 });
  }

  if (showCouponBanner) {
    offers.push({ type: 'couponBanner', order: offers.length + 1 });
  }

  if (showCrossSellBanner) {
    offers.push({ type: 'linkBanner', order: offers.length + 1 });
  }

  if (showMysteryBanner) {
    offers.push({ type: 'mysteryBanner', order: offers.length + 1 });
  }

  if (showPreorderBonus) {
    offers.push({ type: 'preOrderBonus', order: offers.length + 1 });
  }

  if (!isEmpty(partOfBundle)) {
    offers.push({ type: 'partOfBundle', order: offers.length + 1 });
  }

  if (!isEmpty(validVouchers)) {
    offers.push({ type: 'voucher', order: offers.length + 1 });
  }

  if (!isEmpty(validPromotions)) {
    validPromotions.forEach((vp) => {
      offers.push({ type: 'freeProductTrigger', promotionId: vp._id, order: offers.length + 1 });
    });
  }

  if (delivery) {
    offers.push({ type: 'voucherDeliveryTrigger', order: offers.length + 1 });
  }

  if (!emailNewsletter && isEmpty(competition)) {
    offers.push({ type: 'emailNewsletter', order: offers.length + 1 });
  }

  // No offers to show
  if (isEmpty(offers)) {
    return false;
  }

  const bestVoucher = orderBy(validVouchers, 'percent', 'desc')[0];

  const sliderSettings = {
    xs: {
      slidesToShow: 1,
      slidesToScroll: 1,
    },
    sm: {
      slidesToShow: 1,
      slidesToScroll: 1,
    },
    md: {
      slidesToShow: 1,
      slidesToScroll: 1,
    },
    lg: {
      slidesToShow: 2,
      slidesToScroll: 1,
    },
    xl: {
      slidesToShow: 2,
      slidesToScroll: 1,
    },
    xxl: {
      slidesToShow: 2,
      slidesToScroll: 1,
    },
  };

  let sectionTitle = <FormattedMessage id="newproduct.specialoffers.title" defaultMessage="Special Offers" />;
  if (title) sectionTitle = title;

  return (
    <section data-nosnippet className="product-offers-container page-section-margin ProductOffers">
      <Element name="offers">
        <h2 className="section-title pb-2">
          {sectionTitle}
        </h2>
      </Element>

      {offers.length > 0 &&
        <SiteSlider sliderSettings={sliderSettings} sliderClass="ProductOfferCarousel">
          {offers.map(offer =>
            <React.Fragment key={offer.order}>
              {offer.type === 'voucher' &&
                <ValidCoupon voucher={bestVoucher} />
              }
              {offer.type === 'couponBanner' &&
                <CouponBannerOffer />
              }
              {offer.type === 'preOrderBonus' &&
                <PreorderBonus product={product} />
              }
              {offer.type === 'freeProductTrigger' &&
                <FreeProductContainer
                  product={product}
                  promotion={validPromotions.find(vp => vp._id === offer.promotionId)}
                />
              }
              {offer.type === 'linkBanner' &&
                <LinkBannerOffer />
              }
              {offer.type === 'mysteryBanner' &&
                <StaticBannerOffer />
              }
              {offer.type === 'partOfBundle' &&
                <PartOfBundle partOfBundle={partOfBundle} productName={product.name} />
              }
              {offer.type === 'competition' &&
                <EmailSignupPlacement appearsOn="product_offers" />
              }
              {offer.type === 'emailNewsletter' &&
                <EmailSubscribeOffer />
              }
              {offer.type === 'voucherDeliveryTrigger' &&
                <VoucherDelivery product={product} />
              }
            </React.Fragment>,
          )}
        </SiteSlider>
      }
    </section>
  );
}

ProductOffers.propTypes = {
  product: PropTypes.shape({}),
  deliveries: PropTypes.arrayOf(PropTypes.shape({})),
  freeProduct: PropTypes.arrayOf(PropTypes.shape({})),
  vouchers: PropTypes.arrayOf(PropTypes.shape({})),
  title: PropTypes.string,
};

ProductOffers.defaultProps = {
  product: {},
  deliveries: [],
  freeProduct: [],
  vouchers: [],
  title: '',
};

const mapStateToProps = ({
  deliveries: { all: deliveriesAll },
  freeProduct,
  vouchers: { all: vouchersAll },
  auth: { email_newsletter: emailNewsletter },
  competitions,
}) => ({
  deliveries: deliveriesAll,
  freeProduct,
  vouchers: vouchersAll,
  emailNewsletter,
  competitions,
});

export default connect(mapStateToProps, {})(ProductOffers);
